import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark, faChevronLeft, faChevronRight, faPlay, faPause,
} from '@fortawesome/free-solid-svg-icons';

import Act1Onboard from './acts/Act1_Onboard';
import Act2LiveTests from './acts/Act2_LiveTests';
import Act3Portfolio from './acts/Act3_Portfolio';
import Act4Reports from './acts/Act4_Reports';
import Act5Roadmap from './acts/Act5_Roadmap';

import './ShowcasePage.css';

const ACTS = [
  { id: 'onboard',   label: 'Onboarding',   title: 'Any building, onboarded in minutes',  component: Act1Onboard   },
  { id: 'tests',     label: 'Live tests',   title: 'Every test, live, zero paperwork',     component: Act2LiveTests },
  { id: 'portfolio', label: 'Portfolio',    title: 'The whole portfolio, one glance',      component: Act3Portfolio },
  { id: 'reports',   label: 'Reports',      title: 'Proof the auditor wants',              component: Act4Reports   },
  { id: 'roadmap',   label: 'What’s next', title: 'What’s landing next',          component: Act5Roadmap   },
];

function hashToIndex(hash) {
  if (!hash) return 0;
  const m = /act-(\d+)/i.exec(hash);
  if (m) {
    const n = parseInt(m[1], 10) - 1;
    if (n >= 0 && n < ACTS.length) return n;
  }
  const byId = ACTS.findIndex(a => `#${a.id}` === hash);
  return byId >= 0 ? byId : 0;
}

export default function ShowcasePage() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(() => hashToIndex(window.location.hash));
  const [direction, setDirection] = useState(0); // -1 = back, +1 = forward
  const [auto, setAuto] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((next) => {
    setIndex((prev) => {
      const clamped = Math.max(0, Math.min(ACTS.length - 1, next));
      setDirection(clamped > prev ? 1 : clamped < prev ? -1 : 0);
      // update hash without scroll jump
      const id = ACTS[clamped].id;
      if (window.location.hash !== `#${id}`) {
        window.history.replaceState(null, '', `#${id}`);
      }
      return clamped;
    });
  }, []);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  const exit = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }, [navigate]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'Escape') { e.preventDefault(); exit(); }
      else if (e.key >= '1' && e.key <= String(ACTS.length)) {
        goTo(parseInt(e.key, 10) - 1);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [next, prev, exit, goTo]);

  // Hash → state (back/forward browser nav)
  useEffect(() => {
    const onHashChange = () => setIndex(hashToIndex(window.location.hash));
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Auto-play
  useEffect(() => {
    if (!auto) return;
    timerRef.current = setTimeout(() => {
      if (index < ACTS.length - 1) goTo(index + 1);
      else setAuto(false);
    }, 9000);
    return () => clearTimeout(timerRef.current);
  }, [auto, index, goTo]);

  // Ensure the initial hash is present on mount (if user came in bare)
  useEffect(() => {
    if (!window.location.hash) {
      window.history.replaceState(null, '', `#${ACTS[index].id}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = ACTS[index];
  const Act = act.component;

  return (
    <div className="sc-root" data-act={act.id}>
      {/* Ambient background — subtle, restrained */}
      <div className="sc-bg" aria-hidden>
        <div className="sc-bg-grid" />
        <div className="sc-bg-glow sc-bg-glow-teal" />
        <div className="sc-bg-glow sc-bg-glow-navy" />
      </div>

      {/* Top bar */}
      <header className="sc-topbar">
        <div className="sc-topbar-left">
          <span className="sc-wordmark">
            <span className="sc-wordmark-dot" /> CloudSafe
          </span>
          <span className="sc-topbar-sep">/</span>
          <span className="sc-topbar-label">Showcase</span>
        </div>
        <div className="sc-topbar-right">
          <button
            className={`sc-auto-btn ${auto ? 'is-on' : ''}`}
            onClick={() => setAuto(a => !a)}
            type="button"
            title={auto ? 'Pause auto-play' : 'Play through automatically'}
          >
            <FontAwesomeIcon icon={auto ? faPause : faPlay} />
            <span>{auto ? 'Pause' : 'Auto'}</span>
          </button>
          <button className="sc-exit" onClick={exit} type="button" title="Exit tour (Esc)">
            <FontAwesomeIcon icon={faXmark} />
            <span>Exit</span>
          </button>
        </div>
      </header>

      {/* Stage */}
      <main className="sc-stage" aria-live="polite">
        <div
          key={act.id}
          className={`sc-stage-inner sc-slide-${direction >= 0 ? 'fwd' : 'back'}`}
        >
          <Act active onNext={next} onPrev={prev} />
        </div>
      </main>

      {/* Bottom controls */}
      <footer className="sc-footer">
        <button
          className="sc-nav-btn"
          onClick={prev}
          disabled={index === 0}
          type="button"
          aria-label="Previous act"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <span>Prev</span>
        </button>

        <div className="sc-dots" role="tablist" aria-label="Showcase acts">
          {ACTS.map((a, i) => (
            <button
              key={a.id}
              role="tab"
              aria-selected={i === index}
              aria-label={`Act ${i + 1}: ${a.label}`}
              className={`sc-dot ${i === index ? 'is-active' : i < index ? 'is-done' : ''}`}
              onClick={() => goTo(i)}
              type="button"
            >
              <span className="sc-dot-num">{i + 1}</span>
              <span className="sc-dot-label">{a.label}</span>
            </button>
          ))}
        </div>

        <button
          className="sc-nav-btn sc-nav-btn-primary"
          onClick={next}
          disabled={index === ACTS.length - 1}
          type="button"
          aria-label="Next act"
        >
          <span>Next</span>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </footer>

      {/* Progress bar */}
      <div className="sc-progress" aria-hidden>
        <div
          className="sc-progress-fill"
          style={{ width: `${((index + 1) / ACTS.length) * 100}%` }}
        />
      </div>
    </div>
  );
}
